import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  cn,
  InfoPopover,
  Stack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@landler/component-library';
import { ComponentProps, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { MAP_LAYER_FACTS } from '@/api/rest/resources/types/fact';

import { LAYER_OPTIONS, LEGEND_TYPE, LegendConfig } from './constants';
import { useControlsContext } from './hooks/useFormControls';
import { FILTER_OPERATOR, handleFilterUpdate, MapLayerFilter, SetMapLayerFilter } from './hooks/useMapLayerFilter';

type NumericLegendProps = {
  legend: Extract<LegendConfig, { type: typeof LEGEND_TYPE.numeric }>;
};

export const IndexLegend = ({ legend }: NumericLegendProps) => {
  const { t } = useTranslation();

  return (
    <LegendContainer>
      {legend?.unit && (
        <span className='typography-caption text-text-secondary'>
          {t('shared.projects.project.conservation.interactiveMap.legendUnit', { unit: legend.unit })}
        </span>
      )}

      <div className={`h-3 w-full rounded-2xl ${legend.pattern}`} />
      <Stack direction='row' className='justify-between w-full' spacing={2}>
        {legend.values.map((value) => (
          <span key={value} className='typography-caption text-text-secondary'>
            {value}
          </span>
        ))}
      </Stack>
    </LegendContainer>
  );
};

export const CategoryLegend = ({
  legend,
  setLegendFilter,
  legendFilter,
}: {
  legend: Extract<LegendConfig, { type: typeof LEGEND_TYPE.category }>;
  setLegendFilter: SetMapLayerFilter;
  legendFilter: MapLayerFilter;
}) => {
  if (!legend || !legend.values || legend.type !== LEGEND_TYPE.category) return null;

  const hasLegendFilters = legend.hasLegendFilter && legend?.filterKey;

  return (
    <LegendContainer legend={legend}>
      {legend.values.map((item) => (
        <div key={item.text}>
          {hasLegendFilters ? (
            <LegendItemFilterButton
              legend={legend}
              legendItem={item}
              setLegendFilter={setLegendFilter}
              legendFilter={legendFilter}
            />
          ) : (
            <LegendItem legendItem={item} />
          )}
        </div>
      ))}
    </LegendContainer>
  );
};

export const LegendContainer = ({
  children,
  legend,
}: {
  children: ReactNode;
  legend?: Extract<LegendConfig, { type: typeof LEGEND_TYPE.category }>;
}) => {
  return (
    <Stack className='rounded-2xl bg-white-100 p-4 pt-0' spacing={2}>
      <Stack direction='row' spacing={2} className={cn('max-w-[320px] flex-wrap', legend?.className)}>
        {children}
      </Stack>
    </Stack>
  );
};

export const LegendItem = ({
  legendItem,
}: {
  legendItem: {
    text: string;
    color: string;
    tooltip?: string;
  };
}) => {
  const { t } = useTranslation();

  return (
    <Stack
      direction='row'
      key={legendItem.text}
      className='typography-caption text-text-secondary'
      centerMain
      spacing={2}
    >
      <div className='h-2 w-2 rounded-full' style={{ backgroundColor: legendItem.color }} />
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {t(legendItem.text as any)}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {legendItem.tooltip && <InfoPopover body={t(legendItem.tooltip as any)} position='top' />}
    </Stack>
  );
};

const LegendTitle = ({ layerLabel }: { layerLabel: MAP_LAYER_FACTS }) => {
  const { t } = useTranslation();
  const layer = LAYER_OPTIONS.find((opt) => opt.value === layerLabel);
  const isTimeseries = layer?.isTimeSeries;
  const controlsForm = useControlsContext();
  const selectedYear = controlsForm.watch('year');
  return (
    <Stack className='flex flex-row gap-2 justify-between items-center w-full'>
      <span className='typography-body2Semibold text-text-primary'>
        {t(`shared.projects.project.heatMapLayers.${layerLabel}.title`)}
      </span>
      <span className='typography-overline text-text-secondary'>{isTimeseries ? selectedYear : ''}</span>
    </Stack>
  );
};

export const Legend = ({
  id,
  className,
  layerLabel,
  ...delegated
}: ComponentProps<'div'> & { id?: string; layerLabel?: MAP_LAYER_FACTS }) => {
  return (
    <Accordion type='single' collapsible className='bg-white-100 rounded-3xl'>
      <AccordionItem value={id ?? 'legend'} className='rounded-md bg-white-100'>
        <AccordionTrigger
          leftAnchor={true}
          className='px-3 py-2 gap-1 items-start justify-start typography-body2Semibold text-text-primary'
        >
          {layerLabel && <LegendTitle layerLabel={layerLabel} />}
        </AccordionTrigger>
        <AccordionContent className='px-0 py-0'>
          <div className={className} {...delegated} />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

const LegendItemFilterButton = ({
  legend,
  legendItem,
  setLegendFilter,
  legendFilter,
}: {
  legend: Extract<LegendConfig, { type: typeof LEGEND_TYPE.category }>;
  legendItem: Pick<Extract<LegendConfig, { type: typeof LEGEND_TYPE.category }>, 'values'>['values'][number];
  setLegendFilter: SetMapLayerFilter;
  legendFilter: MapLayerFilter;
}) => {
  const { t } = useTranslation();

  if (!legendItem?.filterValue || !legend.filterKey) {
    throw new Error(
      `LegendItemFilterButton cannot have missing legend filterValue or legend filterKey: ${JSON.stringify({ legendItem, legend })}`,
    );
  }

  const isLegendFilterActive = legendFilter.value.includes(legendItem.filterValue);

  const handleClick = () => {
    const newFilters = isLegendFilterActive
      ? legendFilter.value.filter((f) => f !== legendItem.filterValue)
      : [...legendFilter.value, legendItem.filterValue];

    handleFilterUpdate({
      filterKey: legend.filterKey ?? '',
      filterValue: (newFilters ?? []).filter(Boolean) as string[],
      filterOperator: FILTER_OPERATOR.IN,
      setFilters: setLegendFilter,
    });
  };

  const button = (
    <Button
      variant='outline'
      size='small'
      className={cn('py-1 px-2', isLegendFilterActive && 'border-primary-100 bg-primary-12')}
      onClick={handleClick}
    >
      <div className='h-2 w-2 rounded-full' style={{ backgroundColor: legendItem.color }} />
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {t(legendItem.text as any)}
    </Button>
  );

  return (
    <Stack
      direction='row'
      key={legendItem.text}
      className='typography-caption text-text-secondary'
      centerMain
      spacing={1}
    >
      {legendItem.tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <span>{t(legendItem.tooltip as any)}</span>
          </TooltipContent>
        </Tooltip>
      ) : (
        button
      )}
    </Stack>
  );
};
